import { createSlice, createAction, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { RootState } from 'app/state/store'
import { RequestStatus } from 'features/common'
import { ImportingJobPayload, JobsPopperTab, ProcessingJob, ProcessingJobPayload, ProcessingJobStatus } from '../types'
import { isPendingOrRunning } from '../models/jobs'
export const name = 'processingJobs'
export const CREATE_PROCESSING_JOB_REQUEST = `${name}/create_processing_job_request`
export const CANCEL_PROCESSING_JOB_REQUEST = `${name}/cancel_processing_job_request`
export const GET_PROCESSING_JOBS_REQUEST = `${name}/get_processing_jobs_request`
export const GET_USER_PROCESSING_JOBS_REQUEST = `${name}/get_user_processing_jobs_request`
export const CHECK_PROCESSING_JOB_REQUEST = `${name}/check_processing_job_request`
export const FILTER_PROCESSING_JOBS_REQUEST = `${name}/filter_processing_jobs_request`

export interface JobsState {
  jobsPopperOpen: boolean
  jobsPopperTab?: JobsPopperTab
  processingJobs: Array<ProcessingJob>
  processingJobsStatus: RequestStatus
  tableProcessingJobs: Array<ProcessingJob>
  tableProcessingJobsStatus: RequestStatus
  processingJobsInProgress: string[]
  createProcessingJobStatus: RequestStatus
  lastProcessingJobDone?: ProcessingJob
  currentProcessingJob?: ProcessingJob
  optimizationModalOpen: boolean
}

const initialState: JobsState = {
  jobsPopperOpen: false,
  processingJobs: [],
  processingJobsStatus: RequestStatus.Idle,
  tableProcessingJobs: [],
  tableProcessingJobsStatus: RequestStatus.Idle,
  processingJobsInProgress: [],
  createProcessingJobStatus: RequestStatus.Idle,
  optimizationModalOpen: false
}

const asyncActions = {
  createProcessingJobRequest: createAction<{
    connection: string
    name?: string
    processingJobPayload: ProcessingJobPayload
  }>(CREATE_PROCESSING_JOB_REQUEST),
  deleteProcessingJobRequest: createAction<{ job: ProcessingJob }>(CANCEL_PROCESSING_JOB_REQUEST),
  getProcessingJobsRequest: createAction<ImportingJobPayload>(GET_PROCESSING_JOBS_REQUEST),
  getTableProcessingJobsRequest: createAction<ImportingJobPayload & { tableId?: string }>(
    GET_USER_PROCESSING_JOBS_REQUEST
  ),
  checkProcessingJobStatusRequest: createAction<ProcessingJob[]>(CHECK_PROCESSING_JOB_REQUEST),
  clearProcessingJobsRequest: createAction<ProcessingJob[]>(FILTER_PROCESSING_JOBS_REQUEST)
}

const reducers = {
  setJobsPopperState(state: JobsState, action: PayloadAction<{ open?: boolean; tab?: JobsPopperTab }>) {
    const { open, tab } = action.payload
    if (open !== undefined) state.jobsPopperOpen = open
    if (tab !== undefined) state.jobsPopperTab = tab
  },
  addJob: (state: JobsState, action: PayloadAction<ProcessingJob>) => {
    state.processingJobs = [action.payload, ...state.processingJobs]
  },

  updateJob: (state: JobsState, action: PayloadAction<ProcessingJobStatus>) => {
    const tempProcessingJobs = [...state.processingJobs]
    tempProcessingJobs.forEach((j) => {
      if (j.response.externalId === action.payload.jobId) j.status = action.payload
    })
    state.processingJobs = tempProcessingJobs
  },
  setProcessingJobs: (state: JobsState, action: PayloadAction<Array<ProcessingJob>>) => {
    state.processingJobs = action.payload
  },
  setTableProcessingJobs: (state: JobsState, action: PayloadAction<Array<ProcessingJob>>) => {
    state.tableProcessingJobs = action.payload
  },
  removeProcessingJobs: (state: JobsState, action: PayloadAction<Array<ProcessingJob>>) => {
    const removeIds = action.payload.map((job: ProcessingJob) => job.jobId)
    state.processingJobs = state.processingJobs.filter((job: ProcessingJob) => !removeIds.includes(job.jobId))
  },
  setProcessingJobsStatus: (state: JobsState, action: PayloadAction<RequestStatus>) => {
    state.processingJobsStatus = action.payload
  },
  setTableProcessingJobsStatus: (state: JobsState, action: PayloadAction<RequestStatus>) => {
    state.processingJobsStatus = action.payload
  },
  setCreateProcessingJobStatus: (state: JobsState, action: PayloadAction<RequestStatus>) => {
    state.createProcessingJobStatus = action.payload
  },
  setLastProcessingJobDone: (state: JobsState, action: PayloadAction<ProcessingJob | undefined>) => {
    state.lastProcessingJobDone = action.payload
  },
  removeFromProcessingJobs: (state: JobsState, action: PayloadAction<string>) => {
    state.processingJobs = [...state.processingJobs.filter((job) => job.jobId !== action.payload)]
  },
  addProcessingJobInProgress: (state: JobsState, action: PayloadAction<string>) => {
    state.processingJobsInProgress.push(action.payload)
  },
  removeProcessingJobInProgress: (state: JobsState, action: PayloadAction<string>) => {
    state.processingJobsInProgress.filter((jobId) => jobId !== action.payload)
  },
  setCurrentProcessingJob: (state: JobsState, action: PayloadAction<ProcessingJob | undefined>) => {
    state.currentProcessingJob = action.payload
  },
  setOptimizationModalOpen: (state: JobsState, action: PayloadAction<boolean>) => {
    state.optimizationModalOpen = action.payload
  }
}

export const MapsSlice = createSlice({
  name,
  initialState,
  reducers
})

export const areProccesingJobsInProgress = createSelector(
  (state: RootState) => state.jobs.processingJobs,
  (processingJobs) => {
    return processingJobs.some(isPendingOrRunning)
  }
)

export const {
  setJobsPopperState,
  addJob,
  updateJob,
  setProcessingJobs,
  removeProcessingJobs,
  setProcessingJobsStatus,
  setTableProcessingJobs,
  setTableProcessingJobsStatus,
  setCreateProcessingJobStatus,
  setLastProcessingJobDone,
  addProcessingJobInProgress,
  removeProcessingJobInProgress,
  removeFromProcessingJobs,
  setCurrentProcessingJob,
  setOptimizationModalOpen
} = MapsSlice.actions

export const {
  createProcessingJobRequest,
  getProcessingJobsRequest,
  getTableProcessingJobsRequest,
  checkProcessingJobStatusRequest,
  deleteProcessingJobRequest,
  clearProcessingJobsRequest
} = asyncActions

export default MapsSlice.reducer
